<template>
  <ProgramSummaryCardLayout
    :program="program"
    :preview="preview"
    :draft="draft"
    :stacked="stacked"
    :card-state="cardState"
  >
    <template #metric>
      <NumberMetric
        v-if="isClaim"
        size="big"
        :value="earningsValue"
        is-earnings
        :is-dollar-budget="isDollarBudgetResolved"
        :basis="basis"
        class="program-card__earnings-section"
      />
      <div v-else class="program-card__progress-section">
        <div class="program-card__progress-header">
          <Typography variant="subtitle-1" as="span" :class="{ 'text--secondary': isInactive }">
            {{ progressPercent }}%
          </Typography>
          <Typography v-if="progressLabel" variant="body-1" as="span">
            {{ progressLabel }}
          </Typography>
        </div>
        <ProgressBarMetric
          :progress="progressPercent / 100"
          :accessible-name="program.displayName || program.name"
        />
      </div>
    </template>

    <template #actions>
      <template v-if="isClaim && !isTerminal">
        <Button :color="effectiveButtonColor" :style="buttonStyle" @click="onOpenProgram">
          Open Program
        </Button>
        <Button
          v-if="program.uuid"
          outlined
          :color="effectiveButtonColor"
          :style="buttonStyle"
          @click="onSubmitClaim"
        >
          Submit Claim
        </Button>
      </template>
      <Button
        v-else
        :outlined="isTerminal"
        :color="effectiveButtonColor"
        :style="buttonStyle"
        @click="onOpenProgram"
      >
        {{ isTerminal ? "Review" : "Open Program" }}
      </Button>
    </template>
  </ProgramSummaryCardLayout>
</template>

<script>
import ProgramSummaryCardLayout from "./ProgramSummaryCardLayout.vue";
import NumberMetric from "./metrics/NumberMetric.vue";
import ProgressBarMetric from "./metrics/ProgressBarMetric.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import {
  isTerminalState,
  formatEarnings,
  resolveIsDollarBudget,
  buttonStyle,
} from "./programCardState";
import { openClaimForm } from "@/utils/claimForm";

export default {
  name: "LegacySummaryCard",
  components: { ProgramSummaryCardLayout, NumberMetric, ProgressBarMetric, Typography, Button },
  emits: ["open-program"],
  props: {
    program: { type: Object, required: true },
    earnings: { type: [String, Number], default: null },
    basis: { type: String, default: null },
    isDollarBudget: { type: Boolean, default: null },
    buttonColor: { type: String, default: null },
    buttonTextColor: { type: String, default: null },
    preview: { type: Boolean, default: false },
    draft: { type: Boolean, default: false },
    stacked: { type: Boolean, default: false },
  },
  computed: {
    isClaim() {
      return (this.program.type || "").toLowerCase() === "claim";
    },
    isTerminal() {
      return isTerminalState(this.program);
    },
    isInactive() {
      return this.program.programState === "inactive";
    },
    cardState() {
      return this.isInactive ? "inactive" : undefined;
    },
    isDollarBudgetResolved() {
      return resolveIsDollarBudget(this.isDollarBudget, this.program);
    },
    earningsValue() {
      return formatEarnings(this.earnings, this.isTerminal);
    },
    progressPercent() {
      return Math.min(100, Math.max(0, Number(this.program.progressPercent) || 0));
    },
    progressLabel() {
      const state = this.program.programState;
      const percent = Number(this.program.progressPercent);
      if (state === "inactive") return "";
      if (state === "completed" || percent >= 100) {
        return "Great job!";
      }
      if (state === "not_started" || percent === 0) {
        return "Get started!";
      }
      return this.program.progressValue || this.program.progressLabel || "";
    },
    effectiveButtonColor() {
      return this.buttonColor || "brandPrimary";
    },
    buttonStyle() {
      return buttonStyle(this.buttonTextColor);
    },
  },
  methods: {
    onOpenProgram() {
      if (this.preview) return;
      this.$emit("open-program", this.program);
    },
    onSubmitClaim() {
      if (this.preview) return;
      openClaimForm(this.$router, this.program.uuid);
    },
  },
};
</script>

<style scoped>
.program-card__earnings-section {
  width: 100%;
}

.program-card__progress-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.program-card__progress-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
</style>
